import { useQuery } from '@tanstack/react-query';
import type { AiAction, AiSettings, AiStyle } from '@/api';
import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';

/**
 * The AI writing assistant in the compose window (ADR 0065).
 *
 * The rules this file keeps, each of which the owner asked for by name:
 *
 *   - It exists only when the user turned it on with their own key. When the
 *     settings say off (or the server cannot offer it, or the settings cannot
 *     be read), `available` is false and the composer draws NO assistant
 *     controls at all — not a disabled button.
 *   - Nothing is sent until the user presses something, and only what that
 *     action needs: the selection or their own text, their instruction, the
 *     subject, attachment names for a review, and the quoted thread only on a
 *     reply.
 *   - The answer is a preview. Insert / Replace / Discard are the user's; the
 *     assistant never changes the message by itself and never sends it. After
 *     Insert or Replace, Ctrl+Z works (the text went in through the browser's
 *     editing API) and so does the panel's own Undo.
 */

export const AI_STYLES: { style: AiStyle; label: string }[] = [
  { style: 'clearer', label: 'Clearer' },
  { style: 'shorter', label: 'Shorter' },
  { style: 'formal', label: 'More formal' },
  { style: 'friendlier', label: 'Friendlier' },
];

/** What one menu entry asks for. */
export interface AiChoice {
  action: AiAction;
  style?: AiStyle;
}

export function choiceLabel(choice: AiChoice): string {
  switch (choice.action) {
    case 'draft':
      return 'Draft with AI';
    case 'reply':
      return 'Draft a reply';
    case 'proofread':
      return 'Fix grammar & spelling';
    case 'review':
      return 'Review before sending';
    case 'rewrite':
      return `Rewrite: ${AI_STYLES.find((s) => s.style === choice.style)?.label.toLowerCase() ?? 'clearer'}`;
  }
}

/**
 * The quoted earlier messages of a reply, as the composer seeded them: from
 * the "On …, … wrote:" line (or the first `>` line) to the end. '' when the
 * body has no quote — the user deleted it, or this is not a reply.
 */
export function quotedThread(text: string): string {
  const lines = text.split('\n');
  const first = lines.findIndex((line) => line.trimStart().startsWith('>'));
  if (first < 0) return '';
  let start = first;
  let j = first - 1;
  while (j >= 0 && lines[j]!.trim() === '') j -= 1;
  if (j >= 0 && /\bwrote:\s*$/.test(lines[j]!)) start = j;
  return lines.slice(start).join('\n').trim();
}

/** Codes whose fix is in Settings → AI assistant, so the error links there. */
const SETTINGS_CODES = new Set<string>([
  ApiErrorCodes.AIKeyRejected,
  ApiErrorCodes.AINoCredit,
  ApiErrorCodes.AIModelUnavailable,
  ApiErrorCodes.AINotEnabled,
]);

/** Codes where pressing the same button again may well work. */
const RETRY_CODES = new Set<string>([
  ApiErrorCodes.AIOverloaded,
  ApiErrorCodes.AIRateLimited,
  ApiErrorCodes.AIUpstreamError,
  ApiErrorCodes.RateLimited,
  'network_error',
]);

export interface AiProblem {
  message: string;
  /** Anthropic's own words, when there were any. */
  upstream?: string;
  settingsLink: boolean;
  retry: boolean;
}

export function describeProblem(error: unknown): AiProblem {
  if (error instanceof ApiError) {
    const upstream = error.details?.upstream_message;
    return {
      message: error.message,
      ...(typeof upstream === 'string' && upstream !== '' ? { upstream } : {}),
      settingsLink: SETTINGS_CODES.has(String(error.code)),
      retry: RETRY_CODES.has(String(error.code)) || error.isOffline,
    };
  }
  return { message: 'The assistant could not be reached. Try again.', settingsLink: false, retry: true };
}

/** Whether the assistant is on for this user. False on any doubt. */
export function useAiAvailability(): { available: boolean; settings: AiSettings | undefined } {
  const api = useApi();
  const query = useQuery({
    queryKey: queryKeys.aiSettings(),
    queryFn: ({ signal }) => api.ai.settings(signal),
    retry: false,
    staleTime: 60_000,
  });
  const settings = query.data;
  return { available: settings?.enabled === true && settings.keySaved, settings };
}

/*
 * The compose-side hook, useAiAssist, and its state types live in
 * ./useAiAssist.ts: this module is imported by the reading pane (Summarize
 * shares useAiAvailability and describeProblem), so everything in it ships in
 * the entry chunk, and the hook is only ever used by the lazy composer (#208).
 */
export type { AiAssistController, AiComposeContext, AiPhase, AiRunState } from './useAiAssist';
